// @ts-nocheck
/*
 * Port of voicemails/list.js (842 lines). BYTE-IDENTICAL.
 * Server-paginated voicemail list with role-scoped filters:
 *   - CALL_CENTRE_AGENT sees only their own â†’ no agent/group/callcenter/company filters
 *   - CALL_CENTRE_ADMIN loses call-center filter
 *   - SUPERADMIN / SUB_SUPERADMIN / RESELLER gets company filter
 * Date range clamped to 3 months (`MAX_RANGE_MONTHS`), min = 3 months ago.
 * Audio is fetched from the storage gateway with an Authorization header and
 * played from an object URL - the `<audio>` element can't send auth headers,
 * and a private container answers 401 without one.
 * One-inline-play mode (playingId + audioRef) â€” starting a new play stops
 * the previous. Client-side search filters the paginated rows in place.
 * `Sonner` toast in v2's dashboard-widget rewrite is dropped here â€” this
 * port uses `react-toastify` like v1.
 *
 * Note: `dashboard/widgets/VoicemailsPanel.tsx` is a v2-native rewrite
 * (uses lucide/sonner/tanstack) meant to be embedded in the dashboard;
 * this file is the byte-identical routed page for VOICEMAILS.LIST.
 * v1 barrel `index.js` (`export { default as VoicemailsPanel } from "./list"`)
 * is not reproduced â€” routes import this file directly.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Spinner: any = ({ animation: _a, size, variant, className, ...props }: any) => (
  <span
    className={_cx(
      'spinner-border',
      size === 'sm' && 'spinner-border-sm',
      variant && `text-${variant}`,
      className,
    )}
    role="status"
    aria-hidden="true"
    {...props}
  />
);
import { toast } from 'sonner';
import {
  FiVoicemail,
  FiFilter,
  FiCalendar,
  FiUser,
  FiUsers,
  FiHeadphones,
  FiHome,
  FiSearch,
  FiDownload,
  FiRefreshCw,
  FiPhone,
  FiHardDrive,
  FiPlay,
  FiSquare,
} from 'react-icons/fi';
import { getApiCall } from '@/service/httpCall';
import {
  API_VOICEMAILS_LIST,
  API_VOICEMAILS_AUDIO,
  API_AGENTS_LIST,
  API_AGENT_GROUPS_LIST,
  API_CALL_CENTRE_LIST,
  API_COMPANY_LIST,
} from '@/service/url';
import { STORAGE_API_BASE_URL, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { fetchMediaAudioUrl, revokeMediaAudioUrl } from '@/utilities/mediaAudio';
import { storageUrlOr } from '@/utilities/storageUrl';
import { isMediaReady, MEDIA_NOT_READY_TITLE } from '@/utilities/cdrMedia';
import {
  LIST_DEFAULT_PAGE_SIZE,
  LIST_PAGE_SIZE_OPTIONS,
} from '@/utilities/listPagination';
import { exportToCSV } from '@/utilities/helpers';
import './voicemails.css';
import { FillPage } from '@/components/layout/PageContainer';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const TZ = 'Asia/Kolkata';
const MAX_RANGE_MONTHS = 3;

const formatDateOnlyLocal = (date: Date) => {
  const off = date.getTimezoneOffset();
  return new Date(date.getTime() - off * 60000).toISOString().slice(0, 10);
};

const getDefaultDateRange = () => {
  const now = new Date();
  return {
    from: formatDateOnlyLocal(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: formatDateOnlyLocal(now),
  };
};

const getMinAllowedFromDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - MAX_RANGE_MONTHS);
  return formatDateOnlyLocal(d);
};

const isRangeWithinLimit = (fromDate: string, toDate: string) => {
  if (!fromDate || !toDate) return true;
  const from = new Date(`${fromDate}T00:00:00`);
  const maxTo = new Date(from);
  maxTo.setMonth(maxTo.getMonth() + MAX_RANGE_MONTHS);
  const to = new Date(`${toDate}T00:00:00`);
  return to.getTime() <= maxTo.getTime();
};

const DEFAULT_DATE_RANGE = getDefaultDateRange();
const MIN_FROM_DATE = getMinAllowedFromDate();
const TODAY = formatDateOnlyLocal(new Date());

const formatBytes = (bytes: any) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const getInitials = (name: any) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'VM';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const splitDateTime = (value: any) => {
  if (!value) return { date: '-', time: '', isToday: false };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '-', time: '', isToday: false };

  const date = d.toLocaleDateString('en-GB', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: TZ });
  const rowStr = d.toLocaleDateString('en-CA', { timeZone: TZ });

  return { date, time, isToday: todayStr === rowStr };
};

const buildAudioUrl = (row: any) => {
  const path = API_VOICEMAILS_AUDIO.replace(':id', String(row?.id));
  const base = String(STORAGE_API_BASE_URL || '').replace(/\/+$/, '');
  const byId = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  return storageUrlOr('voicemails', row?.blob_path, byId);
};

const buildPageItems = (page: number, totalPages: number) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const set = new Set([1, totalPages, page - 1, page, page + 1]);
  const nums = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const items: any[] = [];
  for (let i = 0; i < nums.length; i += 1) {
    if (i > 0 && nums[i] - nums[i - 1] > 1) items.push('…');
    items.push(nums[i]);
  }
  return items;
};

function VoicemailsList() {
  const user = getUser();
  const role = user?.role;

  const isAgent = role === USER_ROLES.CALL_CENTRE_AGENT;
  const isCCAdmin = role === USER_ROLES.CALL_CENTRE_ADMIN;
  const showCompanyFilter =
    role === USER_ROLES.SUPERADMIN ||
    role === USER_ROLES.SUB_SUPERADMIN ||
    role === USER_ROLES.RESELLER;
  const showCallCenterFilter = !isAgent && !isCCAdmin;
  const showAgentGroupFilter = !isAgent;
  const showAgentFilter = !isAgent;

  const [fromDate, setFromDate] = useState(DEFAULT_DATE_RANGE.from);
  const [toDate, setToDate] = useState(DEFAULT_DATE_RANGE.to);
  const [agentId, setAgentId] = useState('');
  const [agentGroupId, setAgentGroupId] = useState('');
  const [callCenterId, setCallCenterId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [search, setSearch] = useState('');

  const [agents, setAgents] = useState<any[]>([]);
  const [agentGroups, setAgentGroups] = useState<any[]>([]);
  const [callCentres, setCallCentres] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const [playingId, setPlayingId] = useState<any>(null);
  const audioRef = useRef<any>(null);

  useEffect(() => {
    if (showAgentFilter) {
      getApiCall(API_AGENTS_LIST, { fields: 'min' })
        .then((resp: any) => setAgents(Array.isArray(resp) ? resp : []))
        .catch(() => {});
    }
    if (showAgentGroupFilter) {
      getApiCall(API_AGENT_GROUPS_LIST, { fields: 'min' })
        .then((resp: any) => setAgentGroups(Array.isArray(resp) ? resp : []))
        .catch(() => {});
    }
    if (showCallCenterFilter) {
      getApiCall(API_CALL_CENTRE_LIST)
        .then((resp: any) => setCallCentres(Array.isArray(resp) ? resp : []))
        .catch(() => {});
    }
    if (showCompanyFilter) {
      getApiCall(API_COMPANY_LIST)
        .then((resp: any) => setCompanies(Array.isArray(resp) ? resp : []))
        .catch(() => {});
    }
  }, [showAgentFilter, showAgentGroupFilter, showCallCenterFilter, showCompanyFilter]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const validateDates = useCallback(() => {
    if (!fromDate || !toDate) {
      toast.error('From date and To date are required');
      return false;
    }
    if (fromDate < MIN_FROM_DATE) {
      toast.error('From date cannot be earlier than the last 3 months');
      return false;
    }
    if (fromDate > toDate) {
      toast.error('From date cannot be after To date');
      return false;
    }
    if (!isRangeWithinLimit(fromDate, toDate)) {
      toast.error('Date range cannot exceed 3 months');
      return false;
    }
    return true;
  }, [fromDate, toDate]);

  const fetchVoicemails = useCallback(
    async (nextPage = page, nextPageSize = pageSize) => {
      if (!validateDates()) return;

      const params: any = {
        from_date: fromDate,
        to_date: toDate,
        page: nextPage,
        limit: nextPageSize,
      };

      if (showAgentFilter && agentId) params.agent_id = agentId;
      if (showAgentGroupFilter && agentGroupId) {
        params.agent_group_id = agentGroupId;
      }
      if (showCallCenterFilter && callCenterId) {
        params.call_center_id = callCenterId;
      }
      if (showCompanyFilter && companyId) params.company_id = companyId;

      setLoading(true);
      try {
        const data: any = await getApiCall(API_VOICEMAILS_LIST, params);
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(Number(data?.total) || 0);
        setPage(Number(data?.page) || nextPage);
        setPageSize(Number(data?.limit) || nextPageSize);
        setHasFetched(true);
      } catch {
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [
      validateDates,
      fromDate,
      toDate,
      page,
      pageSize,
      agentId,
      agentGroupId,
      callCenterId,
      companyId,
      showAgentFilter,
      showAgentGroupFilter,
      showCallCenterFilter,
      showCompanyFilter,
    ],
  );

  const handleApplyFilters = () => {
    fetchVoicemails(1, pageSize);
  };

  const handleResetFilters = () => {
    setFromDate(DEFAULT_DATE_RANGE.from);
    setToDate(DEFAULT_DATE_RANGE.to);
    setAgentId('');
    setAgentGroupId('');
    setCallCenterId('');
    setCompanyId('');
    setSearch('');
    setRows([]);
    setTotal(0);
    setPage(1);
    setHasFetched(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const handleFromDateChange = (value: string) => {
    if (value && value < MIN_FROM_DATE) {
      toast.error('From date cannot be earlier than the last 3 months');
      setFromDate(MIN_FROM_DATE);
      return;
    }
    setFromDate(value);
  };

  const handleToDateChange = (value: string) => {
    if (fromDate && value && !isRangeWithinLimit(fromDate, value)) {
      toast.error('Date range cannot exceed 3 months');
      return;
    }
    setToDate(value);
  };

  const handlePlay = async (row: any) => {
    if (!isMediaReady(row)) return;
    if (!row?.id) return;

    if (playingId === row.id && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    let objectUrl: string;

    try {
      objectUrl = await fetchMediaAudioUrl(buildAudioUrl(row));
    } catch (err: any) {
      toast.error(err?.message || 'Unable to play voicemail audio');
      return;
    }

    const audio = new Audio(objectUrl);
    audioRef.current = audio;
    setPlayingId(row.id);

    const cleanup = () => {
      revokeMediaAudioUrl(objectUrl);
      setPlayingId(null);
    };

    audio.onended = cleanup;
    audio.onerror = () => {
      toast.error('Unable to play voicemail audio');
      cleanup();
    };
    audio.play().catch(() => {
      toast.error('Unable to play voicemail audio');
      cleanup();
    });
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row: any) => {
      const hay = [
        row.agent_name,
        row.agent_group_name,
        row.call_center_name,
        row.company_name,
        row.did_number,
        row.did,
        row.path,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const pageItems = buildPageItems(page, totalPages);

  const sortedAgents = useMemo(
    () =>
      [...agents].sort((a: any, b: any) =>
        String(a.agent_name || '').localeCompare(String(b.agent_name || '')),
      ),
    [agents],
  );

  const sortedGroups = useMemo(
    () =>
      [...agentGroups].sort((a: any, b: any) =>
        String(a.group_name || '').localeCompare(String(b.group_name || '')),
      ),
    [agentGroups],
  );

  const colSpan =
    5 + (showCallCenterFilter ? 1 : 0) + (showCompanyFilter ? 1 : 0) + 1;

  const handleExport = () => {
    if (!filteredRows.length) {
      toast.info('No voicemails to export');
      return;
    }
    exportToCSV({
      data: filteredRows,
      fileName: `voicemails_${fromDate}_${toDate}`,
      columns: [
        {
          header: 'Date',
          accessor: (row: any) => splitDateTime(row.created_at).date,
        },
        {
          header: 'Time',
          accessor: (row: any) => splitDateTime(row.created_at).time,
        },
        { header: 'Agent', accessor: (row: any) => row.agent_name || '' },
        {
          header: 'Agent Group',
          accessor: (row: any) => row.agent_group_name || '',
        },
        {
          header: 'Call Center',
          accessor: (row: any) => row.call_center_name || '',
        },
        { header: 'Company', accessor: (row: any) => row.company_name || '' },
        {
          header: 'DID',
          accessor: (row: any) => row.did_number || row.did || '',
        },
        { header: 'Size', accessor: (row: any) => formatBytes(row.size) },
      ],
    });
  };

  return (
    <FillPage>
    <div className="vm-page">
      <div className="vm-header">
        <div className="vm-header-icon" aria-hidden>
          <FiVoicemail size={22} />
        </div>
        <div>
          <h1 className="vm-title">Voicemails</h1>
          <p className="vm-subtitle">View and listen to all received voicemails</p>
        </div>
      </div>

      <div className="vm-card">
        <div className="vm-card-head">
          <FiFilter size={16} />
          Filters
        </div>
        <div className="vm-card-body">
          <div className="vm-filters-grid">
            <div>
              <label className="vm-field-label">From Date</label>
              <div className="vm-input-wrap">
                <FiCalendar className="vm-input-icon" size={15} />
                <CalendarDateInput
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e)}
                  min={MIN_FROM_DATE}
                  max={toDate || TODAY}
                />
              </div>
            </div>

            <div>
              <label className="vm-field-label">To Date</label>
              <div className="vm-input-wrap">
                <FiCalendar className="vm-input-icon" size={15} />
                <CalendarDateInput
                  value={toDate}
                  onChange={(e) => handleToDateChange(e)}
                  min={fromDate || MIN_FROM_DATE}
                  max={TODAY}
                />
              </div>
            </div>

            {showAgentFilter && (
              <div>
                <label className="vm-field-label">Agent</label>
                <div className="vm-input-wrap">
                  <FiUser className="vm-input-icon" size={15} />
                  <Form.Select
                    value={agentId}
                    onChange={(e) => setAgentId(e.target.value)}
                  >
                    <option value="">All Agents</option>
                    {sortedAgents.map((a: any) => (
                      <option key={a.id} value={a.id}>
                        {a.agent_name}
                        {a.agent_number ? ` (${a.agent_number})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            )}

            {showAgentGroupFilter && (
              <div>
                <label className="vm-field-label">Agent Group</label>
                <div className="vm-input-wrap">
                  <FiUsers className="vm-input-icon" size={15} />
                  <Form.Select
                    value={agentGroupId}
                    onChange={(e) => setAgentGroupId(e.target.value)}
                  >
                    <option value="">All Agent Groups</option>
                    {sortedGroups.map((g: any) => (
                      <option key={g.id} value={g.id}>
                        {g.group_name || g.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            )}

            {showCallCenterFilter && (
              <div>
                <label className="vm-field-label">Call Center</label>
                <div className="vm-input-wrap">
                  <FiHeadphones className="vm-input-icon" size={15} />
                  <Form.Select
                    value={callCenterId}
                    onChange={(e) => setCallCenterId(e.target.value)}
                  >
                    <option value="">All Call Centers</option>
                    {callCentres.map((cc: any) => (
                      <option key={cc.id} value={cc.id}>
                        {cc.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            )}

            {showCompanyFilter && (
              <div>
                <label className="vm-field-label">Company</label>
                <div className="vm-input-wrap">
                  <FiHome className="vm-input-icon" size={15} />
                  <Form.Select
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                  >
                    <option value="">All Companies</option>
                    {companies.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            )}
          </div>

          <div className="vm-filter-actions">
            <button
              type="button"
              className="vm-btn vm-btn-reset"
              onClick={handleResetFilters}
              disabled={loading}
            >
              <FiRefreshCw size={15} />
              Reset
            </button>
            <button
              type="button"
              className="vm-btn vm-btn-apply"
              onClick={handleApplyFilters}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : <FiFilter size={15} />}
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <div className="vm-card">
        <div className="vm-table-toolbar">
          <h2 className="vm-table-title">
            Voicemails {hasFetched ? <span>({total})</span> : null}
          </h2>
          <div className="vm-toolbar-right">
            <div className="vm-search-wrap">
              <FiSearch size={15} />
              <input
                type="search"
                placeholder="Search voicemails..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="vm-btn vm-btn-ghost"
              onClick={handleExport}
              disabled={!hasFetched || !filteredRows.length}
            >
              <FiDownload size={15} />
              Export
            </button>
          </div>
        </div>

        <div className="vm-table-wrap">
          <table className="vm-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Agent</th>
                <th>Agent Group</th>
                {showCallCenterFilter && <th>Call Center</th>}
                {showCompanyFilter && <th>Company</th>}
                <th>DID</th>
                <th>Size</th>
                <th>Play</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={colSpan}>
                    <div className="vm-empty">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading voicemails…
                    </div>
                  </td>
                </tr>
              ) : !hasFetched ? (
                <tr>
                  <td colSpan={colSpan}>
                    <div className="vm-empty">
                      Set filters and click Apply Filters to load voicemails.
                    </div>
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan}>
                    <div className="vm-empty">
                      No voicemails found for the selected filters.
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row: any) => {
                  const dt = splitDateTime(row.created_at);
                  const agentName = row.agent_name || 'Unknown';
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="vm-date-cell">
                          <span className="vm-date-icon">
                            <FiCalendar size={14} />
                          </span>
                          <div>
                            <div className="vm-cell-main">{dt.date}</div>
                            <span className="vm-cell-sub">{dt.time}</span>
                            {dt.isToday ? (
                              <span className="vm-date-badge">Today</span>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="vm-agent-cell">
                          <span className="vm-avatar">
                            {getInitials(agentName)}
                          </span>
                          <div>
                            <div className="vm-cell-main">{agentName}</div>
                            <span className="vm-cell-sub">Agent</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="vm-cell-main">
                          {row.agent_group_name || 'No Group'}
                        </div>
                        <span className="vm-cell-sub">Agent Group</span>
                      </td>
                      {showCallCenterFilter && (
                        <td>
                          <div className="vm-cell-main">
                            {row.call_center_name || '-'}
                          </div>
                          <span className="vm-cell-sub">Call Center</span>
                        </td>
                      )}
                      {showCompanyFilter && (
                        <td>
                          <div className="vm-cell-main">
                            {row.company_name || '-'}
                          </div>
                          <span className="vm-cell-sub">Company</span>
                        </td>
                      )}
                      <td>
                        <div className="vm-meta-cell">
                          <FiPhone size={14} />
                          <div>
                            <div className="vm-cell-main">
                              {row.did_number || row.did || '-'}
                            </div>
                            <span className="vm-cell-sub">DID Number</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="vm-meta-cell">
                          <FiHardDrive size={14} />
                          <div>
                            <div className="vm-cell-main">
                              {formatBytes(row.size)}
                            </div>
                            <span className="vm-cell-sub">File Size</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`vm-play-btn${
                            playingId === row.id ? ' is-playing' : ''
                          }`}
                          disabled={!isMediaReady(row)}
                          style={isMediaReady(row) ? undefined : { cursor: 'not-allowed', opacity: 0.4 }}
                          onClick={() => handlePlay(row)}
                          title={!isMediaReady(row) ? MEDIA_NOT_READY_TITLE : playingId === row.id ? 'Stop' : 'Play'}
                          aria-label={playingId === row.id ? 'Stop' : 'Play'}
                        >
                          {playingId === row.id ? (
                            <FiSquare size={14} />
                          ) : (
                            <FiPlay size={16} style={{ marginLeft: 2 }} />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {hasFetched && total > 0 && (
          <div className="vm-footer">
            <div className="vm-footer-left">
              <span>Show</span>
              <Form.Select
                value={pageSize}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setPageSize(next);
                  fetchVoicemails(1, next);
                }}
              >
                {LIST_PAGE_SIZE_OPTIONS.map((n: number) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
              <span>entries</span>
            </div>

            <div className="vm-pager">
              <button
                type="button"
                className="vm-page-btn"
                disabled={loading || page <= 1}
                onClick={() => fetchVoicemails(1, pageSize)}
              >
                First
              </button>
              <button
                type="button"
                className="vm-page-btn"
                disabled={loading || page <= 1}
                onClick={() => fetchVoicemails(page - 1, pageSize)}
              >
                Previous
              </button>
              {pageItems.map((item: any, idx: number) =>
                item === '…' ? (
                  <span
                    key={`e-${idx}`}
                    className="vm-page-btn"
                    style={{ border: 'none' }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`vm-page-btn${page === item ? ' is-active' : ''}`}
                    disabled={loading}
                    onClick={() => fetchVoicemails(item, pageSize)}
                  >
                    {item}
                  </button>
                ),
              )}
              <button
                type="button"
                className="vm-page-btn"
                disabled={loading || page >= totalPages}
                onClick={() => fetchVoicemails(page + 1, pageSize)}
              >
                Next
              </button>
              <button
                type="button"
                className="vm-page-btn"
                disabled={loading || page >= totalPages}
                onClick={() => fetchVoicemails(totalPages, pageSize)}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </FillPage>
  );
}

export default VoicemailsList;
